/* STARSEED-CAPMARKETS-293 — the Lead Management search box.

   A phone-only lead's number is its only identifier, so search has to find a
   lead by its digits: typing "4353001520" must find "(435) 300-1520".

   Review round 2 of PR #2404 found the first version compared digits for EVERY
   query. It stripped non-digits from whatever was typed and ORed in a
   phone-contains-digits match, so "mike2@acme.com" also returned every lead
   whose phone contains a 2, and "3M Capital" every phone with a 3. The digit
   comparison now runs only when the query is phone-shaped: nothing but digits
   and the punctuation people type in phone numbers, with at least one digit.

   Review round 3 found the digits were still compared raw, so a number pasted
   with its country code ("+1 435 300 1520") missed the lead stored as
   "(435) 300-1520" — which create's dedupe (normalize_phone) calls the same
   lead. Both sides now normalise the country code the way the API does, and the
   raw comparison is kept alongside it, so a number typed exactly as it is stored
   ("1435300" against "+1 435-300-1520") still matches mid-typing. */

const PHONE_SHAPED = /^[\d\s()+.-]+$/;

/** Mirror of the API's `models/leads.py::normalize_phone`: digits only, and an
 *  11-digit number starting with 1 loses the US country code — so
 *  "+1 435-300-1520" and "(435) 300-1520" reduce to the same "4353001520". The
 *  rule cannot be imported across the Python/JS boundary, so
 *  `leadSearch.test.js` pins the same table of spellings the API tests use. */
export function normalizePhoneDigits(phone) {
  const digits = String(phone ?? "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
}

/** The digits a phone-shaped QUERY stands for. A full number normalises exactly
 *  as the API does; a partial one typed with an explicit country code
 *  ("+1 435", "1-435-300") drops that 1 too, so a caller-ID paste matches while
 *  it is still being typed. A bare leading 1 ("1435") is left alone — without a
 *  "+" or a separator it may be part of the number. */
function queryDigits(query) {
  const q = String(query).trim();
  const withoutCountryCode = /^\+1/.test(q) || /^1[\s.(-]/.test(q) ? q.replace(/^\+?1/, "") : q;
  return normalizePhoneDigits(withoutCountryCode);
}

/** True when `query` can only be a phone number, or part of one. */
export function isPhoneShapedQuery(query) {
  const q = String(query ?? "");
  return PHONE_SHAPED.test(q) && /\d/.test(q);
}

/** Does `lead` match the search box's `query`? An empty query matches everything. */
export function leadMatchesQuery(lead, query) {
  if (!query) return true;
  const q = String(query).toLowerCase();
  if (
    lead.name?.toLowerCase().includes(q) ||
    lead.email?.toLowerCase().includes(q) ||
    lead.company?.toLowerCase().includes(q) ||
    lead.phone?.toLowerCase().includes(q)
  ) {
    return true;
  }
  if (!isPhoneShapedQuery(q) || !lead.phone) return false;
  const typed = q.replace(/\D/g, "");
  if (typed.length > 0 && lead.phone.replace(/\D/g, "").includes(typed)) return true;
  const digits = queryDigits(q);
  return digits.length > 0 && normalizePhoneDigits(lead.phone).includes(digits);
}
